import type { ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ChatProvider } from './context/ChatContext';
import { AlertsProvider } from './context/AlertsContext';
import { Spinner } from './components/ui/Spinner';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import AppRouter from './router/AppRouter';

// Mientras se resuelve si hay una sesión de Firebase persistida (F5, pestaña
// nueva), no se decide "no autenticado" todavía — eso expulsaría al login a
// un usuario con sesión válida antes de tiempo.
function AuthGate({ children }: { children: ReactNode }) {
  const { isInitializing } = useAuth();

  if (isInitializing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  return <>{children}</>;
}

// La frontera de la sesión: TODO provider que guarde datos del tenant va aquí
// dentro, y el `key` lo remonta desde cero cuando cambia la identidad.
//
// Sin esto, un logout seguido de un login de OTRO tenant en la misma pestaña
// dejaba intactos los datos del anterior: estos providers cuelgan por encima
// del router, así que ni el logout ni la navegación los remontan, y sus
// efectos de carga corren una sola vez en la vida de la pestaña (issue #102 —
// con `admin@mindor.demo` se veían las 40 alertas del tenant Paitron). El
// backend nunca las entregó: RLS aísla bien y la API devuelve 0 para ese
// tenant; las filas venían de la memoria del navegador.
//
// La clave lleva el tenant ADEMÁS del usuario. Con el id de usuario solo ya
// bastaría hoy, pero el par hace explícito qué se está aislando, que es lo que
// importa cuando alguien lea esto dentro de seis meses.
function SessionScope({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const sessionKey = user ? `${user.tenantId}:${user.id}` : 'anon';

  return (
    <AlertsProvider key={sessionKey}>
      <ChatProvider>
        {children}
      </ChatProvider>
    </AlertsProvider>
  );
}

export default function App() {
  // El boundary va MÁS AFUERA que todo lo demás: un error dentro de cualquier
  // provider (sesión, tema, alertas, chat) también deja la app en blanco, y
  // desde aquí sí se captura. Ver ErrorBoundary.tsx para lo que NO cubre.
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AuthGate>
              <SessionScope>
                <AppRouter />
              </SessionScope>
            </AuthGate>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
